import type { JSONContent } from "@tiptap/core"
import type { MentionItem } from "@/components/prompt-editor/types"
import {
  flowNodeAudioOutput,
  flowNodeCustomTitle,
  flowNodePlaysVideo,
  flowNodeSubjectName,
  type FlowNodeKind,
  type FlowNodeRow,
} from "@/types/flow"

export type FlowNodeMentionSource = Pick<
  FlowNodeRow,
  | "id"
  | "kind"
  | "title"
  | "shot_label"
  | "entity_name"
  | "media_thumb_url"
  | "media_url"
  | "content_type"
  | "chain_output_media"
>

const NODE_TOKEN_RE = /@node\[(\d+)\]/g

/** Ids of `@node[id]` tokens, in order, without duplicates. */
export function flowNodeMentionIdsFromText(text: string): number[] {
  const ids: number[] = []
  const seen = new Set<number>()
  for (const match of text.matchAll(NODE_TOKEN_RE)) {
    const id = Number(match[1])
    if (!Number.isFinite(id) || seen.has(id)) continue
    seen.add(id)
    ids.push(id)
  }
  return ids
}

/** Chip label: custom title, else shot label, else entity name, else the kind name. */
export function flowNodeMentionLabel(node: FlowNodeMentionSource, kindLabel: string): string {
  if (flowNodeCustomTitle(node.kind)) {
    const title = node.title?.trim()
    if (title) return title
  }
  return flowNodeSubjectName(node) ?? kindLabel
}

function flowNodeMentionMedia(node: FlowNodeMentionSource): {
  imageUrl: string | null
  mediaType?: MentionItem["mediaType"]
} {
  if (node.kind === "audio" || flowNodeAudioOutput(node)) return { imageUrl: null, mediaType: "audio" }
  const playsVideo = flowNodePlaysVideo(node)
  const imageUrl = node.media_thumb_url || (!playsVideo ? node.media_url : null)
  if (imageUrl) return { imageUrl }
  if (
    playsVideo ||
    node.kind === "video" ||
    node.kind === "video_transition" ||
    node.kind === "shot" ||
    node.kind === "video_upscale" ||
    node.kind === "video_depth" ||
    node.chain_output_media === "video"
  ) {
    return { imageUrl: null, mediaType: "video" }
  }
  return { imageUrl: null }
}

export function flowNodeMentionItems(
  nodes: readonly FlowNodeMentionSource[],
  kindLabel: (kind: FlowNodeKind) => string,
): MentionItem[] {
  return nodes.map((node) => {
    const label = kindLabel(node.kind)
    const media = flowNodeMentionMedia(node)
    return {
      category: "node",
      id: node.id,
      label: flowNodeMentionLabel(node, label),
      subtitle: label,
      imageUrl: media.imageUrl,
      ...(media.mediaType ? { mediaType: media.mediaType } : {}),
    }
  })
}

/** Composer document: `@node[id]` becomes a mention chip, other text stays text. */
export function flowNodeMentionTextToDoc(text: string, items: readonly MentionItem[]): JSONContent {
  if (text === "") return { type: "doc", content: [{ type: "paragraph" }] }
  const byId = new Map(items.filter((item) => item.category === "node").map((item) => [item.id, item]))
  return {
    type: "doc",
    content: text.split("\n").map((line) => {
      if (line.length === 0) return { type: "paragraph" }
      const content: JSONContent[] = []
      let last = 0
      for (const match of line.matchAll(NODE_TOKEN_RE)) {
        const index = match.index ?? 0
        if (index > last) content.push({ type: "text", text: line.slice(last, index) })
        const id = Number(match[1])
        const item = byId.get(id)
        content.push({
          type: "mention",
          attrs: {
            category: "node",
            id,
            label: item?.label ?? match[0],
          },
        })
        last = index + match[0].length
      }
      if (last < line.length) content.push({ type: "text", text: line.slice(last) })
      return content.length === 0 ? { type: "paragraph" } : { type: "paragraph", content }
    }),
  }
}

export type FlowNodeMentionPart =
  | { type: "text"; text: string }
  | { type: "mention"; id: number; label: string; imageUrl: string | null; mediaType?: MentionItem["mediaType"] }

/** Split a stored chat message into text and node chips. */
export function splitFlowNodeMentionText(text: string, items: readonly MentionItem[]): FlowNodeMentionPart[] {
  const byId = new Map(items.filter((item) => item.category === "node").map((item) => [item.id, item]))
  const parts: FlowNodeMentionPart[] = []
  let last = 0
  for (const match of text.matchAll(NODE_TOKEN_RE)) {
    const index = match.index ?? 0
    if (index > last) parts.push({ type: "text", text: text.slice(last, index) })
    const id = Number(match[1])
    const item = byId.get(id)
    parts.push({
      type: "mention",
      id,
      label: item?.label ?? match[0],
      imageUrl: item?.imageUrl ?? null,
      ...(item?.mediaType ? { mediaType: item.mediaType } : {}),
    })
    last = index + match[0].length
  }
  if (last < text.length || parts.length === 0) parts.push({ type: "text", text: text.slice(last) })
  return parts
}
